import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineSlotRecipe,
} from '@chakra-ui/react'
import { dialogAnatomy, tableAnatomy } from '@chakra-ui/react/anatomy'

import { config } from '@enechain/ecloud-designsystem'

/**
 * chakra v3 移行に伴い、テーブルを DS の装飾付きコンポーネント（DataTable の
 * Thead/Th/Td）から chakra v3 の素の TableRoot/TableColumnHeader/TableCell へ
 * 置き換えた結果、DS が持っていたテーブルの見た目（ヘッダ行のグレー背景・
 * ヘッダ文字スタイル・行間の枠線）が失われた。
 *
 * それらを全テーブルへ一括復元するため、DS の config に table の slotRecipe を
 * 重ねたローカル system を作る。値は DS の DataTable コンポーネント実装
 * （Surface.secondary のヘッダ背景 / headerSm・#535353・高さ40px のヘッダ /
 * 高さ56px・1px の枠線のセル）に合わせている。
 */
const tableRecipe = defineSlotRecipe({
  slots: tableAnatomy.keys(),
  base: {
    columnHeader: {
      textStyle: 'headerSm',
      textTransform: 'none',
      color: '#535353',
      height: '40px',
      backgroundColor: 'Surface.secondary',
      borderBottomWidth: '1px',
    },
    cell: {
      borderBottomWidth: '1px',
    },
  },
  // chakra 既定の size=md が columnHeader に付ける縦 padding を打ち消し、
  // DS の Th（paddingY: 0 + height 40px + 垂直中央揃え）に合わせる。
  variants: {
    size: {
      md: {
        columnHeader: { paddingY: '0' },
      },
    },
  },
})

/**
 * chakra v2 の ModalHeader は block 表示で、太字 (fontSize: xl / semibold) を
 * Header 自身が持っていた。v3 では header が display: flex になり、太字は
 * DialogTitle スロット側に移ったため、v2 移行のまま素のテキストや
 * justify-between の Flex を DialogHeader 直下に置いている画面 (18ファイル) で
 * 「タイトルが太字でない」「Flex が fit-content 幅になり右寄せが崩れる」が
 * 起きる。v2 相当の header スタイルを復元して一括対応する。
 */
const dialogRecipe = defineSlotRecipe({
  slots: dialogAnatomy.keys(),
  base: {
    header: {
      display: 'block',
      fontSize: 'xl',
      fontWeight: 'semibold',
    },
    // DS の ModalView 系は Positioner を Portal で包まないため、テーブルの
    // 行メニュー内で開くとダイアログが td 内に描画され、テーブル側の
    // white-space: nowrap を継承して本文が折り返さず横にはみ出す。
    // content で継承を断ち切り、どこで開かれても通常の折り返しにする。
    content: {
      whiteSpace: 'normal',
    },
  },
})

const appConfig = defineConfig({
  // 外周の枠線・角丸は「スクロールの外側の固定ラッパー」= TableScrollArea
  // （table を直接内包する overflow ラッパー div）に付ける。
  // <table> 自体に付けると横スクロールで枠が一緒に流れて左右の枠が消えるため。
  // scrollArea には安定した class/data 属性が無いので :has() で table root を持つ
  // 親 div を狙う。overflow(auto) はそのまま活かし、border-radius でスクロール内容を角丸クリップする。
  globalCss: {
    'div:has(> .chakra-table__root)': {
      borderWidth: '1px',
      borderColor: 'Border.lowEmphasis',
      borderRadius: 'radiiMd',
    },
    // v2 の TableContainer は自身に maxWidth: 100% を持っていたが、v3 の
    // DataTable ルート (ScrollArea を包む Box) には無い。そのため
    // alignItems="center" 等の非 stretch な flex 内に置くとルートが
    // テーブルの内容幅まで膨らみ、モーダル等の容器を突き抜ける。
    // ScrollArea の1つ外側のラッパーに maxWidth を戻して容器幅で
    // 頭打ちにし、内側の overflow: auto で横スクロールさせる。
    'div:has(> div > .chakra-table__root)': {
      maxWidth: '100%',
    },
  },
  theme: {
    slotRecipes: {
      dialog: dialogRecipe,
      table: tableRecipe,
    },
  },
})

export const system = createSystem(defaultConfig, config, appConfig)
